import { useEffect, useRef, useState } from 'react';
import type { Group, Mesh, Object3D, OrthographicCamera, Scene, WebGLRenderer } from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { SeatColor } from '@shared/game/types';

export interface Token3DPiece {
  key: string;
  color: SeatColor;
  x: number;
  y: number;
  status: 'home' | 'active' | 'goal';
}

interface Token3DLayerProps {
  pieces: readonly Token3DPiece[];
  animations: boolean;
  onReady: (ready: boolean) => void;
}

const TOKEN_COLORS: Record<SeatColor, number> = {
  coral: 0xff6f7f,
  amber: 0xffc85c,
  jade: 0x58e2b2,
  azure: 0x71dfff,
};

const TOKEN_ASSET = '/recursos 3D parchis/token-blanco.glb';
const TOKEN_HEIGHT = 48;
const DEBUG = process.env.NODE_ENV !== 'production';

interface TokenRuntime {
  group: Group;
  visual: Object3D;
  baseY: number;
  targetX: number;
  targetY: number;
  phase: number;
}

function supportsWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

function disposeGroup(group: Group): void {
  group.traverse((object) => {
    const mesh = object as Mesh;
    if (!mesh.isMesh) return;
    // La geometría del modelo se comparte entre clones; solo liberamos materiales.
    const material = mesh.material;
    if (Array.isArray(material)) material.forEach((item) => item.dispose());
    else material?.dispose();
  });
}

export function Token3DLayer({ pieces, animations, onReady }: Token3DLayerProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const piecesRef = useRef(pieces);
  const animationsRef = useRef(animations);
  const onReadyRef = useRef(onReady);
  const syncRef = useRef<((items: readonly Token3DPiece[]) => void) | null>(null);

  piecesRef.current = pieces;
  animationsRef.current = animations;
  onReadyRef.current = onReady;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    if (!supportsWebGL()) {
      if (DEBUG) console.info('[token-3d] WebGL no disponible; fallback 2D.');
      onReadyRef.current(false);
      return undefined;
    }

    let disposed = false;
    let renderer: WebGLRenderer | null = null;
    let scene: Scene | null = null;
    let camera: OrthographicCamera | null = null;
    let frame = 0;
    let resizeObserver: ResizeObserver | null = null;
    let contextLost = false;
    const tokens = new Map<string, TokenRuntime>();

    const dispose = () => {
      disposed = true;
      syncRef.current = null;
      if (frame) cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      for (const token of tokens.values()) disposeGroup(token.group);
      tokens.clear();
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
        renderer.domElement.remove();
      }
      renderer = null;
      scene = null;
      camera = null;
      setReady(false);
      onReadyRef.current(false);
    };

    const start = async () => {
      try {
        const THREE = await import('three');
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        if (disposed || !host) return;

        renderer = new THREE.WebGLRenderer({
          alpha: true,
          antialias: true,
          powerPreference: 'high-performance',
        });
        renderer.setClearColor(0x000000, 0);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.08;
        renderer.domElement.className = 'token-3d__canvas';
        renderer.domElement.setAttribute('aria-hidden', 'true');
        host.appendChild(renderer.domElement);
        renderer.domElement.addEventListener('webglcontextlost', onContextLost);

        scene = new THREE.Scene();
        // La cámara está centrada en (500,500): el frustum debe ser relativo a ese centro.
        camera = new THREE.OrthographicCamera(-500, 500, 500, -500, 0.1, 2000);
        camera.position.set(500, 500, 700);
        camera.up.set(0, 1, 0);
        camera.lookAt(500, 500, 0);

        scene.add(new THREE.AmbientLight(0xc8f7ff, 1.55));
        const keyLight = new THREE.DirectionalLight(0x9feaff, 2.6);
        keyLight.position.set(280, 1120, 520);
        scene.add(keyLight);
        const warmLight = new THREE.DirectionalLight(0xffbd7a, 1.1);
        warmLight.position.set(820, 160, 430);
        scene.add(warmLight);

        const loader = new GLTFLoader();
        if (DEBUG) console.info('[token-3d] cargando', TOKEN_ASSET);
        const gltf = await loader.loadAsync(encodeURI(TOKEN_ASSET)) as GLTF;
        if (disposed || !scene) return;
        if (DEBUG) console.info('[token-3d] GLB cargado');

        const source = gltf.scene;
        const box = new THREE.Box3().setFromObject(source);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const scale = TOKEN_HEIGHT / Math.max(size.y, 0.001);
        const baseOffset = -box.min.y * scale;

        source.traverse((object) => {
          const mesh = object as Mesh;
          if (!mesh.isMesh) return;
          mesh.castShadow = false;
          mesh.receiveShadow = false;
          mesh.frustumCulled = false;
        });

        const makeMaterial = (color: number) =>
          new THREE.MeshPhysicalMaterial({
            color,
            metalness: 0.12,
            roughness: 0.34,
            clearcoat: 0.5,
            clearcoatRoughness: 0.22,
            emissive: new THREE.Color(color).multiplyScalar(0.32),
            emissiveIntensity: 1.35,
            side: THREE.DoubleSide,
          });

        const createToken = (piece: Token3DPiece): TokenRuntime => {
          const group = new THREE.Group();
          const visual = source.clone(true);
          visual.scale.setScalar(scale);
          visual.position.set(-center.x * scale, baseOffset, -center.z * scale);
          visual.rotation.y = -0.14;
          visual.traverse((object) => {
            const mesh = object as Mesh;
            if (mesh.isMesh) {
              mesh.frustumCulled = false;
              mesh.material = makeMaterial(TOKEN_COLORS[piece.color]);
            }
          });
          group.add(visual);

          const ring = new THREE.Mesh(
            new THREE.RingGeometry(15, 21, 40),
            new THREE.MeshBasicMaterial({
              color: TOKEN_COLORS[piece.color],
              transparent: true,
              opacity: piece.status === 'home' ? 0.22 : 0.42,
              side: THREE.DoubleSide,
              depthWrite: false,
              depthTest: false,
              blending: THREE.AdditiveBlending,
            }),
          );
          ring.position.z = -0.02;
          ring.frustumCulled = false;
          group.add(ring);

          // Disco de facción bajo el anillo Meshy: evita que el centro quede
          // hueco y mantiene legible el color desde cualquier tamaño móvil.
          const disc = new THREE.Mesh(
            new THREE.CylinderGeometry(11.8, 11.8, 2.8, 32),
            new THREE.MeshStandardMaterial({
              color: TOKEN_COLORS[piece.color],
              metalness: 0.32,
              roughness: 0.3,
              emissive: TOKEN_COLORS[piece.color],
              emissiveIntensity: 0.82,
            }),
          );
          disc.rotation.x = Math.PI / 2;
          disc.position.set(0, baseOffset, -1.4);
          disc.frustumCulled = false;
          group.add(disc);

          // Núcleo de facción: da volumen y color propio a cada unidad sin
          // depender de las texturas pesadas de 2048 px.
          const core = new THREE.Mesh(
            new THREE.OctahedronGeometry(7.2, 0),
            new THREE.MeshStandardMaterial({
              color: TOKEN_COLORS[piece.color],
              emissive: TOKEN_COLORS[piece.color],
              emissiveIntensity: 2.6,
              metalness: 0.15,
              roughness: 0.2,
              transparent: true,
              opacity: 0.92,
            }),
          );
          core.position.set(0, baseOffset, 1.2);
          core.frustumCulled = false;
          group.add(core);

          group.position.set(piece.x, 1000 - piece.y, 0);
          scene?.add(group);
          return {
            group,
            visual,
            baseY: baseOffset,
            targetX: piece.x,
            targetY: 1000 - piece.y,
            phase: (piece.x + piece.y) * 0.013,
          };
        };

        const sync = (items: readonly Token3DPiece[]) => {
          const active = new Set(items.map((piece) => piece.key));
          for (const [key, token] of tokens) {
            if (active.has(key)) continue;
            scene?.remove(token.group);
            disposeGroup(token.group);
            tokens.delete(key);
          }
          for (const piece of items) {
            let token = tokens.get(piece.key);
            if (!token) {
              token = createToken(piece);
              tokens.set(piece.key, token);
            }
            token.targetX = piece.x;
            token.targetY = 1000 - piece.y;
          }
        };
        syncRef.current = sync;
        sync(piecesRef.current);

        const resize = () => {
          if (!renderer || !camera || !host) return;
          const width = Math.max(1, host.clientWidth);
          const height = Math.max(1, host.clientHeight);
          renderer.setSize(width, height, false);
          camera.left = -500;
          camera.right = 500;
          camera.top = 500;
          camera.bottom = -500;
          camera.updateProjectionMatrix();
        };
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(host);
        resize();

        let lastFrame = 0;
        const animate = (time: number) => {
          if (disposed || !renderer || !scene || !camera || contextLost) return;
          frame = requestAnimationFrame(animate);
          // 30 fps es suficiente para una capa decorativa y reduce batería en móvil.
          if (time - lastFrame < 32) return;
          lastFrame = time;
          const smooth = animationsRef.current ? 0.14 : 1;
          const seconds = time / 1000;
          for (const token of tokens.values()) {
            token.group.position.x += (token.targetX - token.group.position.x) * smooth;
            token.group.position.y += (token.targetY - token.group.position.y) * smooth;
            token.visual.position.y =
              token.baseY + (animationsRef.current ? Math.sin(seconds * 1.6 + token.phase) * 0.9 : 0);
            token.visual.rotation.z =
              animationsRef.current ? Math.sin(seconds * 0.7 + token.phase) * 0.018 : 0;
          }
          renderer.render(scene, camera);
        };
        frame = requestAnimationFrame(animate);
        if (DEBUG) console.info('[token-3d] capa lista');
        setReady(true);
        onReadyRef.current(true);
      } catch (error) {
        // WebGL/GLB no disponible: se conserva el tablero SVG y las fichas 2D.
        if (DEBUG) console.info('[token-3d] fallback 2D', error instanceof Error ? error.message : '');
        dispose();
      }
    };

    const onContextLost = (event: Event) => {
      event.preventDefault();
      contextLost = true;
      renderer?.domElement.removeEventListener('webglcontextlost', onContextLost);
      dispose();
    };
    void start();

    return () => {
      renderer?.domElement.removeEventListener('webglcontextlost', onContextLost);
      dispose();
    };
  }, []);

  useEffect(() => {
    piecesRef.current = pieces;
    syncRef.current?.(pieces);
  }, [pieces]);

  return (
    <div
      ref={hostRef}
      className={`token-3d ${ready ? 'is-ready' : ''}`}
      aria-hidden="true"
      data-state={ready ? 'ready' : 'fallback'}
    />
  );
}
